import React, { useRef, useState } from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ArrowDownTrayIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  EmptyState,
  SectionCard,
  StatusBadge,
  TWButton,
  ToggleRow,
  TWConfirmModal,
  TWDataTable,
} from '../../../components/ui';
import { DataTableSkeleton, StackedText, colTitle } from '../../../data/columns';
import fleetApi from '../../../services/fleetApi';
import useFleetPoll, { fleetErrorText } from '../use-fleet-poll';
import { APPS } from '../fleet-utils';

const SKELETON_HEADERS = [
  { label: 'Version', w: 'w-32' },
  { label: 'State', shape: 'pill', w: 'w-20' },
  { label: '', align: 'right', w: 'w-16' },
];

// A kiosk or kitchen device takes its own app plus the agent; an unassigned
// one only the agent.
const appsFor = (device) => {
  const own = APPS.find((a) => a.value !== 'agent' && a.value === device.role);
  const agent = APPS.find((a) => a.value === 'agent');
  return own ? [own, agent] : [agent];
};

// One single-device rollout per click: batch of one, no pause threshold and
// the fleet's current release left alone (setscurrent false).
const DeviceInstallTab = ({ device }) => {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useFleetPoll(
    () => fleetApi.listReleases().then((res) => res.data.data || []),
    { intervalMs: 0 },
  );
  const [pending, setPending] = useState(null); // { app, release }
  const [busy, setBusy] = useState(false);
  const [replace, setReplace] = useState(false);
  const busyRef = useRef(false);
  // The confirm text stays readable while the modal fades out.
  const lastPending = useRef(null);
  if (pending) lastPending.current = pending;

  if (error) {
    return (
      <EmptyState
        icon={ExclamationTriangleIcon}
        title="Could not load releases"
        description={fleetErrorText(error)}
        action={<TWButton onClick={reload}>Retry</TWButton>}
      />
    );
  }
  if (loading) return <DataTableSkeleton headers={SKELETON_HEADERS} rows={4} />;

  const releases = data || [];
  const sections = appsFor(device)
    .map((app) => ({
      app,
      installed: device.appversions?.[app.packagename],
      // The current release first, then the others from newest to oldest.
      releases: releases
        .filter((r) => r.app === app.value)
        .sort((a, b) => Number(b.iscurrent) - Number(a.iscurrent) || b.versioncode - a.versioncode),
    }))
    .filter((s) => s.releases.length > 0);

  if (sections.length === 0) {
    return (
      <EmptyState
        icon={ArrowDownTrayIcon}
        title="No releases for this device"
        description="Upload a release to install it from here."
      />
    );
  }

  const close = () => {
    if (!busyRef.current) setPending(null);
  };

  const openInstall = (next) => {
    setReplace(false);
    setPending(next);
  };

  const install = async () => {
    if (!pending || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const body = {
        releaseid: pending.release.id,
        target: { kind: 'devices', deviceids: [device.id] },
        batchsize: 1,
        batchintervalminutes: 0,
        maxfailures: 0,
        setscurrent: false,
      };
      // Sent only when on; the API never replaces the agent itself.
      if (replace && pending.app.value !== 'agent') body.replace = true;
      const res = await fleetApi.createRollout(body);
      message.success('Install queued');
      navigate(`/dashboard/devices/rollouts/${res.data.data.id}`);
    } catch (err) {
      message.error(fleetErrorText(err, 'Could not start the install'));
    } finally {
      busyRef.current = false;
      setBusy(false);
      setPending(null);
    }
  };

  const columnsFor = (installed) => [
    {
      title: colTitle('Version'),
      key: 'version',
      render: (_, r) => <StackedText primary={r.versionname} secondary={`code ${r.versioncode}`} />,
    },
    {
      title: colTitle('State'),
      key: 'state',
      render: (_, r) => (
        <div className="flex items-center gap-1.5">
          {r.iscurrent ? <StatusBadge size="sm" tone="emerald" label="Current" /> : null}
          {installed && installed.code === r.versioncode ? (
            <StatusBadge size="sm" tone="blue" label="Installed" />
          ) : null}
        </div>
      ),
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: 'actions',
      align: 'right',
      render: (_, r) => (
        <TWButton
          variant="default"
          size="sm"
          onClick={() => openInstall({ app: sectionApp(r.app), release: r })}
        >
          Install
        </TWButton>
      ),
    },
  ];

  const shown = pending || lastPending.current;
  const shownRelease = shown?.release;

  return (
    <div className="space-y-6">
      {sections.map(({ app, installed, releases: appReleases }) => (
        <SectionCard
          key={app.value}
          title={app.label}
          description={
            installed ? `Installed ${installed.name} (${installed.code})` : 'Not installed'
          }
          noBodyPadding
        >
          <TWDataTable columns={columnsFor(installed)} data={appReleases} />
        </SectionCard>
      ))}

      <TWConfirmModal
        open={Boolean(pending)}
        onClose={close}
        onConfirm={install}
        loading={busy}
        tone="warning"
        title={`Install ${shown ? shown.app.label : ''} ${shownRelease?.versionname || ''} on this device?`}
        description="The app restarts during the install and an order in progress can be lost. This does not change the fleet's current release."
        confirmLabel="Install"
      >
        {shown && shown.app.value !== 'agent' ? (
          <ToggleRow
            title="Replace if the installed app's signature differs"
            description="Loses the app's data; the kiosk must be paired again."
            enabled={replace}
            onChange={setReplace}
            disabled={busy}
          />
        ) : null}
      </TWConfirmModal>
    </div>
  );
};

const sectionApp = (value) => APPS.find((a) => a.value === value);

export default DeviceInstallTab;
